import React, { useState } from 'react'
import { AlertTriangle, CheckCircle2, XCircle, ChevronDown, ChevronUp } from 'lucide-react'

const LEVEL_STYLES = {
  faible: {
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    dot: 'bg-emerald-500',
    bar: 'bg-emerald-500',
    icon: CheckCircle2,
  },
  modere: {
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
    dot: 'bg-amber-500',
    bar: 'bg-amber-500',
    icon: AlertTriangle,
  },
  eleve: {
    badge: 'bg-red-50 text-red-700 border-red-200',
    dot: 'bg-red-500',
    bar: 'bg-red-500',
    icon: XCircle,
  },
}

export function ScoreBadge({ score, niveau, size = 'sm', showLabel = true }) {
  if (score === undefined || score === null) return null
  const code = niveau?.code || (score <= 30 ? 'faible' : score <= 60 ? 'modere' : 'eleve')
  const label = niveau?.label || (score <= 30 ? 'Faible' : score <= 60 ? 'Modéré' : 'Élevé')
  const style = LEVEL_STYLES[code] || LEVEL_STYLES.faible

  if (size === 'xs') {
    return (
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold rounded border ${style.badge}`} data-testid="score-badge">
        <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
        {score}
      </span>
    )
  }

  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-bold rounded-lg border ${style.badge}`} data-testid="score-badge">
      <span className={`w-2 h-2 rounded-full ${style.dot}`} />
      <span>{score}</span>
      {showLabel && <span className="font-medium text-[10px] opacity-75">/ 100</span>}
    </span>
  )
}

export function ScoreDetail({ scoring }) {
  const [open, setOpen] = useState(false)
  if (!scoring) return null

  const { score, niveau, composantes, convention, explications } = scoring
  const code = niveau?.code || 'faible'
  const style = LEVEL_STYLES[code] || LEVEL_STYLES.faible

  return (
    <div className="space-y-2">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-[10px] text-slate-500 hover:text-slate-700 transition-colors"
        data-testid="score-detail-toggle"
      >
        {open ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
        Détail du score
      </button>

      {open && (
        <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-3" data-testid="score-detail-panel">
          {/* Explanations */}
          {explications?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {explications.map((e, i) => (
                <span key={i} className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${style.badge}`}>
                  {e}
                </span>
              ))}
            </div>
          )}

          {/* Component bars */}
          {composantes && Object.entries(composantes).map(([key, comp]) => (
            <div key={key} className="space-y-0.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-medium text-slate-600 capitalize">{key.replace('_', ' ')}</span>
                <span className="text-[10px] text-slate-400">{comp.pts} / {comp.max}</span>
              </div>
              <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${style.bar}`}
                  style={{ width: `${(comp.pts / comp.max) * 100}%` }}
                />
              </div>
            </div>
          ))}

          {/* Convention */}
          {convention && (convention.irsa > 0 || convention.irca > 0 || convention.hors_convention > 0) && (
            <div className="pt-1 border-t border-slate-100">
              <p className="text-[10px] font-medium text-slate-500 mb-1">Convention</p>
              <div className="flex gap-2">
                {convention.irsa > 0 && <span className="px-1.5 py-0.5 text-[10px] bg-blue-50 text-blue-600 rounded">IRSA: {convention.irsa}</span>}
                {convention.irca > 0 && <span className="px-1.5 py-0.5 text-[10px] bg-purple-50 text-purple-600 rounded">IRCA: {convention.irca}</span>}
                {convention.hors_convention > 0 && <span className="px-1.5 py-0.5 text-[10px] bg-slate-100 text-slate-600 rounded">Hors conv: {convention.hors_convention}</span>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
